/* jokers-std-default.js — pre-select Standard Shipping in the cart drawer.
   2026-10-02. Loaded by ONE line in layout/theme.liquid, after jokers-cart.js.
   Rollback = delete that line.

   WHY: csOuterfunction() (inline ShipZip block in theme.liquid) strips the
   Standard tab's .active after EVERY widget render, so the checkout button
   stays disabled until the customer opens Delivery Option and picks one —
   even though jokers-cart.js defaultRouting has already stamped every line
   `_odm: Shipping`. Drawer → checkout is 45% (GA4, 28 days to Oct 1 2026).

   WHAT: after each ShipZip render, if NO choice has been made, trigger
   ShipZip's own click on the Standard tile (li[rel="cs-tab1"]). ShipZip's
   handler then marks it active, sets Type Of Order = Shipping, and calls
   sbzMethodChange('cs-tab1'), which enables checkout. jokers-cart.js still
   requires the Conditions of Sale tick.

   NEVER OVERRIDES A CHOICE. Skips when any of these hold:
     - an Express/Premium or Pickup tab is already active
     - any cart line carries _odm other than "Shipping" (Express/Premium
       writes "Local Delivery", pickup writes "Store Pickup")
     - cart attribute "Type Of Order" is set to anything but "Shipping"
     - the cart holds a balloon (pickup only)
     - the Standard tile is hidden, or needs extra input (.order_must)
     - jokers-cart.js has a removal or a write in flight (retries later)
   NEVER clicks "Select": that button carries name="checkout" and ShipZip's
   own checkout handler can submit the cart form from it.
   Loop guard: at most one auto-select per 0.8s, max 12 per 30s (v25).

   v2 2026-10-02: while a default is being decided the drawer carries
   html.jk-std-pending, which hides the orange "Please select a Delivery
   Option" prompt and the pulsing ring on the Delivery Option tab (both set
   by csOuterfunction + jokers-cart.js section 7) so they don't flash for the
   ~300ms before Standard lands. Cleared on every exit path + 2.5s failsafe.
   Note now sits above the Conditions of Sale line. Delays cut to ~60ms.

   v3 2026-10-02 (user direction):
   - Standard note is now a card (#jk-std-note .jk-std): truck icon,
     "Standard Shipping" + green SELECTED pill, sub-line, and a Change
     button that opens the Delivery Option modal.
   - Terms: jokers-cart.js's "Please accept the Conditions of Sale to
     proceed." line (#jokers-terms-message) is hidden in the drawer; instead
     the "I have read and agreed…" label itself pulses in an orange box
     whenever that message WOULD show. Driven purely off that element's
     state, so jokers-cart.js gating logic is untouched. The delivery-option
     variant of the message is left alone.

   v5 2026-10-02: quantity change / remove showed the OLD footer for ~1s
   (no card, "Please accept…", "Please select a Delivery Option…", orange
   tab ring) while the cart request + ShipZip re-render ran. Now:
   - #jokers-terms-message is ALWAYS hidden in the drawer (the pulsing
     label replaces it; ShipZip's own [data-message] still covers delivery).
   - Any click/change on a cart line starts a HOLD: mask on, evaluate waits
     for ShipZip's re-render (csOuterfunction / mount mutation), 4s failsafe.
   - While masked, if Standard was the last state, the card is re-inserted
     immediately into the swapped footer so it never disappears.
*/
(function () {
  if (window.__jkStdDefault) { return; }
  window.__jkStdDefault = true;

  var TAB = 'li.cs-tab_last[rel="cs-tab1"]';
  var lastRun = 0, runLog = [], MAX_RUNS = 12, RUN_WINDOW = 30000, timer = null, failsafe = null;

  /* Pending state: hide the delivery prompt + tab ring while we decide. */
  (function () {
    var st = document.createElement('style');
    st.textContent =
      'html.jk-std-pending #CartDrawer [data-message]{visibility:hidden !important;}' +
      'html.jk-std-pending .jk-alert-tab::after{display:none !important;}' +
      /* v21: a complete method is showing in the box - ShipZip's 'please select'
         line and the pulsing tab ring are noise while it saves */
      'html.jk-has-method #CartDrawer [data-message]{display:none !important;}' +
      'html.jk-has-method .jk-alert-tab::after{display:none !important;}' +
      /* Standard Shipping card */
      '#CartDrawer .drawer__footer-bottom{padding-top:12px !important;}' +
      /* Subtotal label + amount on one line */
      '#CartDrawer .totals{display:flex !important;align-items:baseline;justify-content:flex-end;gap:10px;flex-wrap:nowrap;}' +
      '#CartDrawer .totals__subtotal-value{white-space:nowrap;}' +
      /* Delivery Option panel open: hide the card + terms behind it (mobile showed them through) */
      '#CartDrawer:has([aria-controls^="ShippingOption-"][aria-expanded="true"]) #jk-std-note,' +
      '#CartDrawer:has([id^="ShippingOption-"][open]) #jk-std-note{visibility:hidden !important;}' +
      /* Mobile: compact footer so the item list keeps its room */
      '@media (max-width:767px){' +
        '#CartDrawer .drawer__footer-bottom{gap:10px !important;padding-top:8px !important;}' +
        '#jk-std-note.jk-std{margin:0;padding:7px 10px;gap:10px;border-width:1.5px;}' +
        '.jk-std__icon svg{width:20px;height:20px}' +
        '.jk-std__title{font-size:14px}' +
        '.jk-std__sub{font-size:12px}' +
        /* v25: keep the date/time line on mobile for Express + Pickup */
        '#jk-std-note[data-kind="exp"] .jk-std__sub,#jk-std-note[data-kind="pickup"] .jk-std__sub{display:block !important;font-size:12px}' +
        /* v26: title + pill on one line on mobile; long store names truncate with an ellipsis */
        '.jk-std__title{flex-wrap:nowrap;gap:6px}' +
        '.jk-std__tt{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}' +
        '.jk-std__pill{flex:0 0 auto;font-size:10px;padding:2px 6px}' +
        '.jk-std__change{padding:5px 10px;font-size:12px}' +
        '#CartDrawer .jk-dd{font-size:13px;padding:4px 10px;}' +
        '#CartDrawer .jk-prog{gap:5px}' +
      '}' +
      '.jk-top:empty{display:none}' +
      '#jk-std-note .jk-std__title{flex-wrap:nowrap}' +
      '#jk-std-note .jk-std__pill{order:-1;flex:0 0 auto}' +
      '#jk-std-note .jk-std__tt{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}' +
      /* v28 091026: card stays inside the drawer on mobile (long store names pushed Change + checkout off screen) */
      '#jk-std-note.jk-std{min-width:0;max-width:100%;box-sizing:border-box}' +
      '#jk-std-note .jk-std__body{grid-template-columns:minmax(0,1fr)}' +
      '#jk-std-note .jk-std__title{min-width:0;flex-wrap:wrap;row-gap:2px}' +
      '#jk-std-note .jk-std__tt{white-space:normal;overflow:visible}' +
      /* Pull the despatch pill up under the Cart header */
      '#CartDrawer .drawer__scrollable{padding-top:14px !important;}' +
      '#CartDrawer .jk-top{margin-top:0 !important;}' +
      '#jk-std-note.jk-std{display:flex;align-items:center;gap:12px;margin:0;padding:10px 14px;' +
        'background:#eef8f1;border:2px solid #1f8a4c;border-radius:10px;color:#14301f;}' +
      '.jk-std__icon{flex:0 0 auto;color:#1f8a4c;display:flex}' +
      '.jk-std__body{flex:1 1 auto;min-width:0;display:grid;gap:2px}' +
      '.jk-std__title{font-size:15px;font-weight:700;line-height:1.25;display:flex;align-items:center;gap:8px;flex-wrap:wrap}' +
      '.jk-std__pill{font-size:11px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;' +
        'background:#1f8a4c;color:#fff;border-radius:999px;padding:2px 8px;line-height:1.4}' +
      '.jk-std__pill.jk-std__pill--default{background:transparent;color:#1f8a4c;box-shadow:inset 0 0 0 1.5px #1f8a4c}' +
      '.jk-std__sub{font-size:13px;line-height:1.3;color:#2f4a3a}' +
      /* v9 CONFIRM state: method saved on the cart but ShipZip dropped the tab after a redraw */
      '#jk-std-note.jk-std--confirm{background:#fff6e6;border-color:#F49F11;color:#3a2a08;animation:jkConfirm 1.6s ease-in-out infinite}' +
      '#jk-std-note.jk-std--confirm .jk-std__icon{color:#c77700}' +
      '#jk-std-note.jk-std--confirm .jk-std__sub{color:#5a4210}' +
      '.jk-std__pill.jk-std__pill--confirm{background:#F49F11;color:#22384D}' +
      '#jk-std-note.jk-std--confirm .jk-std__change{color:#22384D;border-color:#F49F11;background:#F49F11}' +
      '@keyframes jkConfirm{0%,100%{box-shadow:0 0 0 0 rgba(244,159,17,.55)}50%{box-shadow:0 0 0 5px rgba(244,159,17,0)}}' +
      '@media (prefers-reduced-motion:reduce){#jk-std-note.jk-std--confirm{animation:none}}' +
      /* the confirm card IS the prompt: hide ShipZip's duplicate line + the tab ring */
      'html.jk-std-confirm #CartDrawer [data-message]{display:none !important;}' +
      'html.jk-std-confirm .jk-alert-tab::after{display:none !important;}' +
      '.jk-std__change{flex:0 0 auto;font:inherit;font-size:13px;font-weight:700;color:#1f8a4c;' +
        'background:#fff;border:1.5px solid #1f8a4c;border-radius:6px;padding:6px 12px;cursor:pointer;line-height:1.2}' +
      '.jk-std__change:hover{background:#1f8a4c;color:#fff}' +
      /* Terms: label is the prompt. Permanent padding + transparent border so
         nothing jumps when it lights up. */
      /* v4: the WORDS pulse orange (no box) while unticked */
      '#CartDrawer label[for^="CartFormInput-"].jk-terms-flash span,' +
      '#CartDrawer label[for^="CartFormInput-"].jk-terms-flash a{animation:jkTermsText 1.4s ease-in-out infinite;}' +
      '@keyframes jkTermsText{0%,100%{color:#ffffff}50%{color:#F49F11}}' +
      '@media (prefers-reduced-motion:reduce){#CartDrawer label.jk-terms-flash span,#CartDrawer label.jk-terms-flash a{animation:none;color:#F49F11}}' +
      '#jokers-terms-message.jk-hide,#CartDrawer #jokers-terms-message{display:none !important;}';
    document.head.appendChild(st);
  })();
  var holdRender = false, lastStd = false;
  function isPending() { return document.documentElement.classList.contains('jk-std-pending'); }
  function pending(on, ms) {
    var h = document.documentElement;
    if (on) {
      h.classList.add('jk-std-pending');
      clearTimeout(failsafe);
      failsafe = setTimeout(function () {
        h.classList.remove('jk-std-pending');
        if (holdRender) { holdRender = false; schedule(0); }
        updateNote();
      }, ms || 2500);
    } else {
      clearTimeout(failsafe);
      h.classList.remove('jk-std-pending');
    }
  }
  function done() {
    pending(false); updateNote();
    if (!document.querySelector('#csShippingAppCode ' + TAB + '.active')) { refreshMethod(300); }
  }
  /* v16: the customer's LAST explicit tab pick this session (trusted clicks only). */
  function lastPick() { try { return sessionStorage.getItem('jkPick') || ''; } catch (x) { return ''; } }
  function userPicked() { try { return sessionStorage.getItem('jkStdUser') === '1'; } catch (x) { return false; } }

  function jq() { return window.csJq || window.jQuery || null; }

  function schedule(delay) {
    if (timer) { clearTimeout(timer); }
    timer = setTimeout(function () { timer = null; evaluate(); }, delay || 60);
  }

  function cartHasLines() {
    return !!document.querySelector('#CartDrawer li[id^="CartDrawer-Item-"]');
  }

  function anyTabActive() {
    return !!document.querySelector('#csShippingAppCode .cs-tabs .cs-tab_last.active');
  }

  function tileUsable(li) {
    if (!li) { return false; }
    if (li.style.display === 'none' || getComputedStyle(li).display === 'none') { return false; }
    if (document.querySelector('#cs-tab1 .order_must')) { return false; }
    return true;
  }

  function cartAllowsDefault(cart) {
    if (!cart || !cart.items || !cart.items.length) { return false; }
    var routed = false;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if ((it.product_type || '').indexOf('Balloon') !== -1) { return false; }
      var odm = it.properties && it.properties._odm;
      if (odm && odm !== 'Shipping') { return false; }
      if (odm) { routed = true; }
    }
    /* v13: the LINES decide checkout rates, so they win. The cart attribute
       'Type Of Order' only matters while lines are bare (e.g. just stripped by a
       quantity change) - then the routing guard rebuilds them from it. A cart
       whose lines all say Shipping but whose attribute is stale gets Standard. */
    if (!routed) {
      var t = cart.attributes && cart.attributes['Type Of Order'];
      if (t && t !== 'Shipping') { return false; }
    }
    return true;
  }

  /* v11 STEP 3 FIX: keep Express/Premium selected across drawer redraws.
     Cause: every drawer redraw empties #csShippingAppCode, jokers-cart.js
     reinitShipZip() calls MainCode() and ShipZip rebuilds the widget from the
     PAGE-LOAD cart. Its own restore (csDefaultSelectedValue) can never restore
     Local Delivery because csCartProperties is never populated - so tab2 drops
     and csOuterfunction locks checkout.
     Fix: when the LIVE cart already carries _odm "Local Delivery" + _odate,
     put ShipZip's tab2 UI + hidden fields back exactly as they were and unlock
     checkout. Touches ONLY classes, .val() and the disabled attr - no tab
     click, no datepicker API, no csSearchOrSelect - so ShipZip writes nothing
     (its write paths are the date onSelect, time-slot and radio-card clicks).
     jokers-cart.js still gates checkout on the Conditions of Sale tick. */
  function restoreExpress(cart) {
    var $j = jq();
    if (!$j || !cart || !cart.items) { return false; }
    /* v16: never restore Express over a newer explicit choice. Switching
       Express -> Standard: ShipZip re-renders mid-save while the cart still
       says Local Delivery; restoring then put tab2 + its hidden inputs back and
       ShipZip saved Express again - checkout showed Express. */
    var lp = lastPick();
    if (lp && lp !== 'cs-tab2') { return false; }
    var line = null, saved = true;
    for (var i = 0; i < cart.items.length; i++) {
      var pr = cart.items[i].properties || {};
      if (pr._odm === 'Local Delivery' && pr._odate) { line = pr; break; }
    }
    /* v18: after a quantity change ShipZip strips the line and the routing
       guard only rebuilds it several seconds later. The choice is still in the
       CART ATTRIBUTES the whole time, so restore the screen from those at once
       (only when no line carries a different method) and keep checkout locked
       until the lines are saved back to Local Delivery. */
    if (!line) {
      var a = cart.attributes || {};
      var other = cart.items.some(function (it) { var o = (it.properties || {})._odm; return o && o !== 'Local Delivery'; });
      if (!other && a['Type Of Order'] === 'Local Delivery' && a['Delivery Date']) {
        line = { _odate: a['Delivery Date'], _odd: a['Delivery Day'] || '', _odt: a['Delivery Time'] || '', _LocationId: a['LocationId'] || '' };
        saved = false;
      }
    }
    if (!line) { return false; }
    var tab = document.querySelector('#csShippingAppCode li.cs-tab_last[rel="cs-tab2"]');
    if (!tab || tab.style.display === 'none') { return false; }
    try {
      $j('#csShippingAppCode .cs-tab_last').removeClass('active');
      $j(tab).addClass('active');
      $j('#csShippingAppCode .cs-tab_content').hide();
      $j('#cs-tab2').show();
      $j('#cs-tab2 .tab-inner-content').show();
      $j('#csOrderTypeValue').val('Local Delivery');
      $j('#csDeliveryDateValue').val(line._odate);
      if (line._odd) { $j('#csDeliveryDayValue').val(line._odd); }
      if (line._odt) { $j('#csDeliveryTimeValue').val(line._odt); }
      if (line._LocationId) { $j('#csLocationId').val(line._LocationId); }
      try { var pc = localStorage.getItem('_cspcod_'); if (pc) { $j('#postal_code').val(pc); } } catch (x) {}
      $j('#cs-tab2 .datePicker').val(line._odate);           /* plain .val(): fires no onSelect */
      $j('#required-error').hide();
      $j('#CartDrawer [data-message]').hide();
      if (saved) { setCheckout(false); } else { holdExpCheckout(); }
      cartMethod = { kind: 'exp', date: line._odate };
      return true;
    } catch (e) { return false; }
  }

/* v14: when Standard is selected (by us or the customer), make sure the cart
     attribute 'Type Of Order' says Shipping. A stale 'Local Delivery' left from
     an earlier choice let the routing guard rebuild Express onto the line after
     ShipZip's next rewrite - Express came back without being chosen.
     Attribute-only write, ?csapp=shipcs so ShipZip does not re-render.
     v27 2026-10-09: ShipZip's Standard tile click blanks its hidden fields but
     never saves the cart, so THIS write is the only thing that records a switch
     to Standard - and it sent Type Of Order alone. The earlier method's Pickup
     Location / Day / Date / Time / Address, Delivery Date / Day / Time and
     LocationId stayed on the cart and reached the order (Jokers68768OS: Shipping
     + the full Rocklea pickup block). Now blanks them in the same write;
     Shopify deletes an attribute sent as "". Also runs when Type Of Order is
     already Shipping but stale details remain. */
  var attrTimer = null;
  var STD_CLEAR = /^(pick.?up|delivery (date|day|time)$|locationid$)/i;
  function syncStdAttr(delay) {
    clearTimeout(attrTimer);
    attrTimer = setTimeout(function () {
      if (!document.querySelector('#csShippingAppCode ' + TAB + '.active')) { return; }
      fetch('/cart.js?csapp=shipcs', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (c) {
          if (!c || !c.items || !c.items.length) { return; }
          var a = c.attributes || {};
          var t = a['Type Of Order'];
          var attrs = {}, stale = 0;
          Object.keys(a).forEach(function (k) {
            if (a[k] && STD_CLEAR.test(k)) { attrs[k] = ''; stale++; }
          });
          var fixType = !!t && t !== 'Shipping';
          if (!fixType && !stale) { return; }
          if (lastPick() !== 'cs-tab1') {
            for (var i = 0; i < c.items.length; i++) {
              var o = (c.items[i].properties || {})._odm;
              if (o && o !== 'Shipping') { return; }   /* a real non-Standard line: leave it */
            }
          }
          if (!document.querySelector('#csShippingAppCode ' + TAB + '.active')) { return; }
          if (fixType) { attrs['Type Of Order'] = 'Shipping'; }
          return fetch('/cart/update.js?csapp=shipcs', {
            method: 'POST', credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ attributes: attrs })
          });
        })
        .catch(function () {});
    }, delay || 1500);
  }

  /* v16 backstop: the customer explicitly chose Standard, but once ShipZip's
     writes have gone quiet a line still carries a non-Shipping _odm (Express
     would then be offered at checkout). Rewrite those lines to Standard, one at
     a time, preserving every other property. Runs only after 2s of no cart XHR
     and no jokers-cart.js writes in flight, at most twice per pick. */
  var lastCartXhr = 0, fixTries = 0, fixTimer = null;
  function enforceStdLines(delay) {
    clearTimeout(fixTimer);
    fixTimer = setTimeout(function () {
      if (lastPick() !== 'cs-tab1') { return; }
      var JK = window.JokersCart;
      if ((JK && (JK.writes > 0 || JK.removing)) || Date.now() - lastCartXhr < 2000) { enforceStdLines(1200); return; }
      if (fixTries >= 2) { return; }
      fetch('/cart.js?csapp=shipcs', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (c) {
          if (!c || !c.items || lastPick() !== 'cs-tab1') { return; }
          var bad = c.items.filter(function (it) { var o = (it.properties || {})._odm; return o && o !== 'Shipping'; });
          if (!bad.length) { return; }
          fixTries++;
          var chain = Promise.resolve();
          bad.forEach(function (it) {
            chain = chain.then(function () {
              var pr = {}, src = it.properties || {};
              Object.keys(src).forEach(function (k) { pr[k] = src[k]; });
              pr._odm = 'Shipping'; pr._odate = ''; pr._odd = ''; pr._odt = ''; pr._LocationId = '';
              return fetch('/cart/change.js?csapp=shipcs', {
                method: 'POST', credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({ id: it.key, quantity: it.quantity, properties: pr })
              });
            });
          });
          return chain.then(function () { syncStdAttr(200); refreshMethod(300); });
        })
        .catch(function () {});
    }, delay || 5000);
  }

  /* v17: ShipZip unlocks checkout the instant Standard is clicked (sbzMethodChange
     cs-tab1), BEFORE its cart write lands. Switching Express -> Standard left a
     window where checkout went through with the line still on Local Delivery.
     Hold checkout disabled until every line reads _odm "Shipping" (the
     enforceStdLines backstop fixes stragglers). Polls the cart; releases after
     15s at most so a slow app can never block a sale outright. jokers-cart.js's
     observer treats this disabled flag like ShipZip's, so the terms gate still
     applies once released. */
  var stdHoldTimer = null, stdHoldUntil = 0;
  function setCheckout(disabled, saving) {
    var $j = jq(); if (!$j) { return; }
    $j(window.checkout_selectors || '#cartDrawerForm [data-checkout]').filter('[data-checkout], #cartDrawerForm button[name="checkout"]').attr('disabled', disabled);
    savingLabel(!!(disabled && saving));
  }
  /* v19: "Saving delivery…" on the drawer's Check out button while one of our
     holds waits for ShipZip's cart write. Swaps only the TEXT inside .btn-text
     (icon kept); the button element, its attributes and listeners are untouched. */
  function savingLabel(on) {
    var b = document.querySelector('#cartDrawerForm [data-checkout]');
    var t = b && (b.querySelector('.btn-text') || b);
    if (!t) { return; }
    var node = null;
    for (var i = 0; i < t.childNodes.length; i++) {
      if (t.childNodes[i].nodeType === 3 && t.childNodes[i].nodeValue.trim()) { node = t.childNodes[i]; break; }
    }
    if (!node) { return; }
    if (on) {
      if (!t.hasAttribute('data-jk-orig')) { t.setAttribute('data-jk-orig', node.nodeValue); }
      if (node.nodeValue !== 'Saving delivery…') { node.nodeValue = 'Saving delivery…'; }
    } else if (t.hasAttribute('data-jk-orig')) {
      node.nodeValue = t.getAttribute('data-jk-orig');
      t.removeAttribute('data-jk-orig');
    }
  }
  function holdStdCheckout() {
    stdHoldUntil = Date.now() + 15000;
    clearTimeout(stdHoldTimer);
    var tick = function () {
      if (lastPick() !== 'cs-tab1') { savingLabel(false); return; }  /* customer moved on: ShipZip owns it */
      setCheckout(true, true);
      fetch('/cart.js?csapp=shipcs', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (c) {
          var ok = c && c.items && c.items.length && c.items.every(function (it) { return (it.properties || {})._odm === 'Shipping'; });
          if (ok || Date.now() > stdHoldUntil) {
            if (document.querySelector('#csShippingAppCode ' + TAB + '.active')) { setCheckout(false); } else { savingLabel(false); }
            return;
          }
          stdHoldTimer = setTimeout(tick, 700);
        })
        .catch(function () { stdHoldTimer = setTimeout(tick, 900); });
    };
    setTimeout(tick, 60);   /* after ShipZip's own click handler has enabled it */
  }

  /* v22: Store Pickup equivalent of restoreExpress. After a quantity change the
     pickup tab drops (ShipZip only restores pickup from the PAGE-LOAD cart).
     Rebuild ShipZip's tab3 state from the saved line, or from the cart
     attributes while the routing guard re-saves the line: tab + store card
     (class only - no click, which would make ShipZip write), hidden fields,
     and the cart form's checkout action with the store's address (what
     ShipZip's card click sets, so checkout pre-fills the pickup address) plus
     jkSetPickupAction(true). Checkout stays locked until lines say Store Pickup. */
  /* v23: ShipZip restores the Store Pickup TAB on page load (back from
     checkout, reload) but not its date/time, so it keeps checkout locked and
     shows its prompt. Treat that as incomplete and let restorePickup fill it. */
  function pickupIncomplete() {
    if (!document.querySelector('#csShippingAppCode li.cs-tab_last[rel="cs-tab3"].active')) { return false; }
    var lp = lastPick(); if (lp && lp !== 'cs-tab3') { return false; }
    var pd = document.getElementById('csPickupDateValue');
    var msg = document.querySelector('#CartDrawer [data-message]');
    var msgOn = !!(msg && msg.offsetParent !== null);
    var cob = document.querySelector('#cartDrawerForm [data-checkout]');
    var cbx = document.querySelector('#CartDrawer input[id^="CartFormInput-"]');
    return !(pd && pd.value) || msgOn || !!(cob && cob.disabled && cbx && cbx.checked);
  }

  function restorePickup(cart) {
    var $j = jq();
    if (!$j || !cart || !cart.items) { return false; }
    var lp = lastPick();
    if (lp && lp !== 'cs-tab3') { return false; }
    var a = cart.attributes || {};
    var pickA = function (re) { for (var k in a) { if (re.test(k) && a[k]) { return a[k]; } } return ''; };
    var src = null, saved = true;
    for (var i = 0; i < cart.items.length; i++) {
      var pr = cart.items[i].properties || {};
      if (pr._odm === 'Store Pickup' && pr._LocationId) { src = { id: String(pr._LocationId), date: pr._odate || '', day: pr._odd || '', time: pr._odt || '' }; break; }
    }
    if (!src) {
      var other = cart.items.some(function (it) { var o = (it.properties || {})._odm; return o && o !== 'Store Pickup'; });
      if (!other && a['Type Of Order'] === 'Store Pickup' && a['LocationId']) {
        src = { id: String(a['LocationId']), date: pickA(/pick.?up.*date/i), day: pickA(/pick.?up.*day/i), time: pickA(/pick.?up.*time/i) };
        saved = false;
      }
    }
    if (!src) { return false; }
    var tab = document.querySelector('#csShippingAppCode li.cs-tab_last[rel="cs-tab3"]');
    if (!tab || tab.style.display === 'none') { return false; }
    var card = document.querySelector('#cs-location-list .cs-radio-card[data-location-mainid="' + src.id + '"]');
    if (!card || card.style.display === 'none') { return false; }   /* store no longer offered: let ShipZip ask */
    try {
      var rd = window.result_data, loc = null;
      if (rd && rd.locations) { for (var j = 0; j < rd.locations.length; j++) { if (String(rd.locations[j].id) === src.id) { loc = rd.locations[j]; break; } } }
      var name = (loc && loc.name) || a['Pickup Location'] || ($j(card).find('h6').text() || '').trim();
      $j('#csShippingAppCode .cs-tab_last').removeClass('active');
      $j(tab).addClass('active');
      $j('#csShippingAppCode .cs-tab_content').hide();
      $j('#cs-tab3').show();
      $j('#cs-tab3 .tab-inner-content').show();
      $j('#cs-location-list .cs-radio-card').removeClass('csactive');
      $j(card).addClass('csactive');
      $j('#csOrderTypeValue').val('Store Pickup');
      $j('#csLocationId').val(src.id);
      $j('#csDeliveryLocationValue').val(name);
      if (a['Pickup Address']) { $j('#csLocationAddress1').val(a['Pickup Address']); }
      if (src.date) { $j('#csPickupDateValue').val(src.date); }
      if (src.day) { $j('#csPickupDayValue').val(src.day); }
      if (src.time) { $j('#csPickupTimeValue').val(src.time); }
      if (loc) {
        var g = (rd.generalSettingData || {}), locale = '';
        if (g.prepopulate_address && String(g.prepopulate_address).indexOf('3') > -1) { locale = g.prepopulate_pickup_text || ''; }
        var url = '/cart?step=contact_information&checkout[shipping_address][address1]=' + (loc.address1 || '') +
          '&checkout[shipping_address][address2]=' + (loc.address2 || '') + '&checkout[shipping_address][city]=' + (loc.city || '') +
          '&checkout[shipping_address][zip]=' + (loc.zip || '') + '&checkout[shipping_address][country]=' + (loc.country_code || '') +
          '&checkout[shipping_address][province]=' + (loc.state_code || '') + '&locale=' + locale;
        if (window.unsetCheckoutInfo == null) { $j(window.csCartFormSelector || '#cartDrawerForm').attr('action', url); }
      }
      if (typeof window.jkSetPickupAction === 'function') { try { window.jkSetPickupAction(true); } catch (x) {} }
      $j('#required-error').hide();
      $j('#CartDrawer [data-message]').hide();
      if (saved) { setCheckout(false); } else { holdMethodCheckout('Store Pickup', 'cs-tab3'); }
      cartMethod = { kind: 'pickup', loc: name, date: src.date, time: src.time };
      return true;
    } catch (e) { return false; }
  }

  /* v18: checkout stays locked until every line is saved back to Local Delivery
     (guard rebuild after a quantity change). 15s cap, then released. */
  var expHoldTimer = null;
  function holdExpCheckout() { holdMethodCheckout('Local Delivery', 'cs-tab2'); }
  function holdMethodCheckout(odmVal, rel) {
    var until = Date.now() + 15000;
    clearTimeout(expHoldTimer);
    var tick = function () {
      var lp = lastPick(); if (lp && lp !== rel) { savingLabel(false); return; }
      if (!document.querySelector('#csShippingAppCode li.cs-tab_last[rel="' + rel + '"].active')) { savingLabel(false); return; }
      setCheckout(true, true);
      fetch('/cart.js?csapp=shipcs', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (c) {
          var ok = c && c.items && c.items.length && c.items.every(function (it) { return (it.properties || {})._odm === odmVal; });
          if (ok || Date.now() > until) { setCheckout(false); return; }
          expHoldTimer = setTimeout(tick, 700);
        })
        .catch(function () { expHoldTimer = setTimeout(tick, 900); });
    };
    tick();
  }

  function startHold() {
    if (!cartHasLines()) { return; }
    holdRender = true;
    pending(true, 4000);
  }
  function endHold() { holdRender = false; }

  function evaluate() {
    if (holdRender) { return; }   /* waiting for ShipZip's re-render */
    var JK = window.JokersCart;
    if (JK && (JK.removing || JK.writes > 0)) { schedule(150); return; }
    var li = document.querySelector('#csShippingAppCode ' + TAB);
    /* v24: ShipZip restored Store Pickup (page load / back from checkout) but
       without its date - complete it NOW, behind the mask, instead of waiting
       for the next cart refresh (that left the prompt pulsing for seconds). */
    if (li && pickupIncomplete()) {
      pending(true);
      fetch('/cart.js?csapp=shipcs', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (c) { if (c) { readMethod(c); restorePickup(c); } done(); })
        .catch(function () { done(); });
      return;
    }
    if (!li || anyTabActive()) { done(); return; }
    if (!tileUsable(li)) { done(); return; }
    /* v25 2026-10-15: loop guard is now max 12 auto-selects per 30s. It was 12 per
       page view and never reset, so repeated adds/removes on one page used it up and
       the drawer stopped defaulting to Standard (pulsing Delivery Option) until a
       reload. When the cap is hit it waits for the window to clear, then tries again. */
    var nowT = Date.now();
    runLog = runLog.filter(function (t) { return nowT - t < RUN_WINDOW; });
    if (runLog.length >= MAX_RUNS) { done(); schedule(RUN_WINDOW - (nowT - runLog[0]) + 50); return; }
    var wait = 800 - (Date.now() - lastRun);
    if (wait > 0) { schedule(wait); return; }  /* throttled: retry, don't drop */
    pending(true);

    fetch('/cart.js?csapp=shipcs', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        /* Re-check after the await — the customer may have clicked a tab. */
        var li2 = document.querySelector('#csShippingAppCode ' + TAB);
        if (!li2 || anyTabActive() || !tileUsable(li2)) { done(); return; }
        if (!cartAllowsDefault(cart)) { readMethod(cart); if (!anyTabActive()) { restoreExpress(cart) || restorePickup(cart); } done(); return; }
        var $j = jq();
        if (!$j) { done(); return; }
        lastRun = Date.now(); runLog.push(lastRun);
        $j(li2).trigger('click');
        syncStdAttr(1500);
        /* ShipZip enables checkout + hides the prompt in its click handler;
           drop the pending mask a beat later so nothing flashes between. */
        setTimeout(done, 120);
      })
      .catch(function () { done(); /* fail closed: leave the drawer as ShipZip set it */ });
  }

  /* Standard Shipping card, above the Conditions of Sale line. */
  var TRUCK = '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M1 4h14v12H1z"/><path d="M15 9h4l3 3v4h-7z"/><circle cx="5.5" cy="18.5" r="1.8"/><circle cx="18.5" cy="18.5" r="1.8"/></svg>';

  /* v14: open a drawer panel and make sure it is really open. aria-expanded can
     flip to true while the modal stays shut (Change showed nothing on desktop),
     so check the modal's own [open] state and fall back to its show(). */
  function openPanel(btn, id) {
    btn.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
    setTimeout(function () {
      var m = document.getElementById(id);
      if (m && !m.hasAttribute('open') && typeof m.show === 'function') {
        try { m.show(btn); } catch (x) { try { m.show(); } catch (y) {} }
      }
    }, 200);
  }

  function openDeliveryOption() {
    var btn = null;
    var list = document.querySelectorAll('#CartDrawer [aria-controls^="ShippingOption-"]');
    for (var i = 0; i < list.length; i++) {
      if (!list[i].closest('[id^="ShippingOption-"]')) { btn = list[i]; break; }
    }
    if (!btn) { return; }
    openPanel(btn, btn.getAttribute('aria-controls'));
  }

  /* v8: the card shows whichever method is in force, not just Standard.
     Standard comes from the live tab (ShipZip strips it on every render, we
     re-pick it). Express/Premium and Pickup are NOT restored as active tabs
     after a re-render, so they come from the cart's own line properties
     (_odm / _odate / _odt / _LocationId) — i.e. only once ShipZip has
     actually saved the choice. */
  var STORE = '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 9l1.5-5h15L21 9"/><path d="M3 9h18v2a3 3 0 0 1-6 0 3 3 0 0 1-6 0 3 3 0 0 1-6 0z"/><path d="M5 13v7h14v-7"/><path d="M10 20v-4h4v4"/></svg>';
  var LOC = { '21709': 'Melbourne', '21710': 'Rocklea', '65573': 'Southport' };
  var cartMethod = null, mTimer = null;
  function esc(t) { return String(t).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }
  function readMethod(cart) {
    cartMethod = null;
    if (!cart || !cart.items) { return; }
    for (var i = 0; i < cart.items.length; i++) {
      var pr = cart.items[i].properties || {};
      if (pr._odm === 'Local Delivery') { cartMethod = { kind: 'exp', date: pr._odate || pr._odd || '' }; return; }
      if (pr._odm === 'Store Pickup') { cartMethod = { kind: 'pickup', loc: LOC[String(pr._LocationId || '')] || '', date: pr._odate || pr._odd || '', time: pr._odt || '' }; return; }
    }
  }
  function refreshMethod(delay) {
    clearTimeout(mTimer);
    mTimer = setTimeout(function () {
      if (document.querySelector('#csShippingAppCode ' + TAB + '.active')) { updateNote(); return; }
      fetch('/cart.js?csapp=shipcs', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (c) {
          if (c) {
            readMethod(c);
            if (!anyTabActive()) { restoreExpress(c) || restorePickup(c); }
            else if (pickupIncomplete()) { restorePickup(c); }
          }
          updateNote();
        })
        .catch(function () {});
    }, delay || 400);
  }

  function cardFor(kind, confirm) {
    if (kind === 'std') {
      var mine = userPicked();
      return { icon: TRUCK, title: 'Standard Shipping', pill: mine ? 'Selected' : 'Default', outline: !mine,
               sub: 'Need it faster? Tap Change for Express, Premium or Store Pickup' };
    }
    if (confirm) {
      return { icon: kind === 'exp' ? TRUCK : STORE, confirm: true,
               title: kind === 'exp' ? 'Express or Premium' : 'Store Pickup' + (cartMethod.loc ? ' — ' + cartMethod.loc : ''),
               pill: 'Confirm', outline: false,
               sub: kind === 'exp' ? 'Tap Change to confirm your delivery date' : 'Tap Change to confirm your pickup time' };
    }
    if (kind === 'exp') {
      return { icon: TRUCK, title: 'Express or Premium', pill: 'Selected', outline: false,
               sub: (cartMethod.date ? 'Required by ' + cartMethod.date + ' - ' : '') + 'Tap Change to select a different option' };
    }
    return { icon: STORE, title: 'Store Pickup' + (cartMethod.loc ? ' — ' + cartMethod.loc : ''), pill: 'Selected', outline: false,
             sub: cartMethod.date ? 'Pickup ' + cartMethod.date + (cartMethod.time ? ', ' + cartMethod.time : '') : 'Collect from our ' + (cartMethod.loc || 'store') };
  }

  function updateNote() {
    var anchor = document.querySelector('#CartDrawer label[for^="CartFormInput-"]') ||
                 document.getElementById('cartDrawerForm');
    if (!anchor) { return; }
    var note = document.getElementById('jk-std-note');
    if (note && note.nextElementSibling !== anchor) { note.parentNode.removeChild(note); note = null; }
    var active = !!document.querySelector('#csShippingAppCode ' + TAB + '.active');
    var masked = isPending();
    if (!masked) { lastStd = active; }
    if (document.querySelector('#csShippingAppCode .cs-tab_last.active:not([rel="cs-tab1"])')) { lastStd = false; }
    /* While masked, keep showing the last confirmed Standard state. */
    var std = active || (masked && lastStd);
    var otherTab = document.querySelector('#csShippingAppCode .cs-tab_last.active:not([rel="cs-tab1"])');
    var kind = std ? 'std' : (cartMethod ? cartMethod.kind : null);
    /* v15: show Express the moment the customer presses Select - read ShipZip's
       own on-screen tab + date instead of waiting 4-5s for its serial cart writes.
       (Checkout itself still unlocks only when ShipZip has saved - by design.) */
    if (!std && otherTab && otherTab.getAttribute('rel') === 'cs-tab2') {
      var dv = document.getElementById('csDeliveryDateValue');
      var d = dv && dv.value;
      if (d) { cartMethod = { kind: 'exp', date: d }; kind = 'exp'; }
    }
    /* v20: same for Store Pickup - ShipZip fills #csDeliveryLocationValue (store
       name) and #csPickupDateValue / #csPickupTimeValue when a store + date are
       chosen, so show the pickup box from those immediately. */
    if (!std && otherTab && otherTab.getAttribute('rel') === 'cs-tab3') {
      var lv = document.getElementById('csDeliveryLocationValue');
      var pd = document.getElementById('csPickupDateValue');
      var pt = document.getElementById('csPickupTimeValue');
      if (lv && lv.value) {
        var cm = (cartMethod && cartMethod.kind === 'pickup') ? cartMethod : {};
        cartMethod = { kind: 'pickup', loc: lv.value, date: (pd && pd.value) || cm.date || '', time: (pt && pt.value) || cm.time || '' };
        kind = 'pickup';
      }
    }
    /* A different tab is open but not yet saved to the cart: show nothing rather than a stale method */
    if (!std && otherTab && cartMethod) {
      var want = otherTab.getAttribute('rel') === 'cs-tab3' ? 'pickup' : 'exp';
      if (want !== cartMethod.kind) { kind = null; }
    }
    /* Saved on the cart but no tab active = ShipZip lost the selection on a redraw
       and is holding checkout. Say so (CONFIRM) instead of claiming SELECTED. */
    /* v10 (owner): no CONFIRM card. If ShipZip has dropped an Express/Pickup
       selection, show no card at all and leave ShipZip's own prompt - same as live. */
    var confirm = false;
    if (kind && kind !== 'std' && !otherTab && !masked) { kind = null; }
    if (!kind) {
      document.documentElement.classList.remove('jk-has-method');
      if (note) { note.parentNode.removeChild(note); }
      syncTerms();
      return;
    }
    /* v21: complete = Standard, or Express/Pickup with a date. While ShipZip is
       still saving one of those (checkout disabled, terms ticked), hide its prompt
       + ring and say "Saving delivery..." on the button instead. */
    var complete = kind === 'std' || ((kind === 'exp' || kind === 'pickup') && cartMethod && cartMethod.date);
    document.documentElement.classList.toggle('jk-has-method', !!complete);
    var cbx = document.querySelector('#CartDrawer input[id^="CartFormInput-"]');
    var cob = document.querySelector('#cartDrawerForm [data-checkout]');
    if (kind === 'exp' || kind === 'pickup') {
      savingLabel(!!(complete && cob && cob.disabled && cbx && cbx.checked));
    }
    var c = cardFor(kind, confirm);
    var sig = kind + '|' + c.title + '|' + c.pill + '|' + c.sub;
    if (!note) {
      note = document.createElement('div');
      note.id = 'jk-std-note';
      note.className = 'jk-std';
      note.setAttribute('role', 'status');
      anchor.parentNode.insertBefore(note, anchor);
    }
    if (note.getAttribute('data-sig') !== sig) {
      note.setAttribute('data-sig', sig);
      note.setAttribute('data-kind', kind);
      note.classList.toggle('jk-std--confirm', !!c.confirm);
      note.innerHTML =
        '<span class="jk-std__icon">' + c.icon + '</span>' +
        '<span class="jk-std__body">' +
          '<span class="jk-std__title"><span class="jk-std__tt">' + esc(c.title) + '</span> <span class="jk-std__pill' + (c.outline ? ' jk-std__pill--default' : '') + (c.confirm ? ' jk-std__pill--confirm' : '') + '" data-jk-pill>' + c.pill + '</span></span>' +
          '<span class="jk-std__sub">' + esc(c.sub) + '</span>' +
        '</span>' +
        '<button type="button" class="jk-std__change">Change</button>';
      note.querySelector('.jk-std__change').addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation(); openDeliveryOption();
      });
    }
    syncTerms();
  }

  /* Terms: mirror #jokers-terms-message (jokers-cart.js) onto the label. */
  var TERMS_PREFIX = 'Please accept';
  function syncTerms() {
    var label = document.querySelector('#CartDrawer label[for^="CartFormInput-"]');
    var msg = document.getElementById('jokers-terms-message');
    if (!label) { return; }
    var isTerms = false;
    if (msg) {
      var txt = (msg.textContent || '').trim();
      isTerms = msg.style.display !== 'none' && txt.indexOf(TERMS_PREFIX) === 0;
      if (msg.classList.contains('jk-hide') !== isTerms) { msg.classList.toggle('jk-hide', isTerms); }
      if (!msg.__jkObs) {
        msg.__jkObs = true;
        new MutationObserver(syncTerms).observe(msg, { attributes: true, attributeFilter: ['style'], childList: true, subtree: true, characterData: true });
      }
    }
    var cb = document.getElementById(label.getAttribute('for'));
    if (cb && cb.checked) { isTerms = false; }
    if (label.classList.contains('jk-terms-flash') !== isTerms) { label.classList.toggle('jk-terms-flash', isTerms); }
  }
  document.addEventListener('change', function (e) {
    if (e.target && e.target.matches && e.target.matches('#CartDrawer input[id^="CartFormInput-"]')) {
      setTimeout(syncTerms, 0); setTimeout(syncTerms, 200); setTimeout(updateNote, 250);
    }
  }, true);

  /* Trigger 1: wrap csOuterfunction — it runs after every ShipZip render
     and is what strips the Standard selection. */
  if (typeof window.csOuterfunction === 'function') {
    var orig = window.csOuterfunction;
    window.csOuterfunction = function () {
      if (cartHasLines()) { pending(true); }   /* before orig shows the prompt */
      var r = orig.apply(this, arguments);
      endHold();
      watchMount();
      schedule(60);
      return r;
    };
  }

  /* Trigger 2: the mount's children are replaced on each render. Observe
     the mount only (never subtree / documentElement — see v6 notes). */
  function watchMount() {
    var mount = document.getElementById('csShippingAppCode');
    if (!mount || mount.__jkStdObs) { return; }
    mount.__jkStdObs = true;
    new MutationObserver(function () { endHold(); if (cartHasLines() && !anyTabActive()) { pending(true); } schedule(60); }).observe(mount, { childList: true });
  }
  watchMount();

  /* Drawer swaps (qty/remove re-render the section): while masked, put the
     card straight back and re-sync the terms label. One rAF per batch. */
  (function () {
    var cd = document.getElementById('CartDrawer');
    if (!cd || !window.MutationObserver) { return; }
    var raf = 0;
    new MutationObserver(function () {
      if (raf) { return; }
      raf = requestAnimationFrame(function () {
        raf = 0;
        watchMount();
        /* updateNote is idempotent: re-adds the card to a freshly swapped
           footer whenever Standard is active (or masked + last state), and
           re-syncs the terms label. The theme can swap the footer AFTER
           ShipZip re-selects, which dropped the card (v6 fix). */
        updateNote();
        if (!document.querySelector('#csShippingAppCode ' + TAB + '.active')) { refreshMethod(700); }
      });
    }).observe(cd, { childList: true, subtree: true });
  })();

  /* "Excludes Shipping" link opens the same Delivery Estimate panel as the
     tab (data-jk-open = that button's aria-controls). Falls back to the
     shipping policy href if the button can't be found. */
  document.addEventListener('click', function (e) {
    var a = e.target && e.target.closest && e.target.closest('#CartDrawer [data-jk-open]');
    if (!a) { return; }
    var id = a.getAttribute('data-jk-open');
    var list = document.querySelectorAll('#CartDrawer [aria-controls="' + id + '"]');
    var btn = null;
    for (var i = 0; i < list.length; i++) { if (!list[i].closest('#' + id)) { btn = list[i]; break; } }
    if (!btn) { return; }
    e.preventDefault(); e.stopPropagation();
    openPanel(btn, id);
  }, true);

  /* Trigger 4: quantity / remove on a cart line → hold until re-render. */
  var LINE = '#CartDrawer li[id^="CartDrawer-Item-"]';
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (t && t.closest && t.closest(LINE + ' button, ' + LINE + ' [is="cart-remove-button"], ' + LINE + ' a[href*="/cart/change"]')) { startHold(); }
  }, true);
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (t && t.closest && t.closest(LINE) && t.matches('input')) { startHold(); }
  }, true);

  /* Trigger 3: customer changes tab → refresh the note only. Drawer open →
     re-check (the drawer section may have been swapped). */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) { return; }
    var tab = t.closest('#csShippingAppCode .cs-tab_last');
    if (tab) {
      /* Only a real customer click counts (our auto-select is isTrusted=false) */
      if (e.isTrusted) {
        try {
          sessionStorage.setItem('jkPick', tab.getAttribute('rel') || '');
          if (tab.getAttribute('rel') === 'cs-tab1') { sessionStorage.setItem('jkStdUser', '1'); syncStdAttr(2500); fixTries = 0; enforceStdLines(5000); holdStdCheckout(); }
          else { sessionStorage.removeItem('jkStdUser'); }
        } catch (x) {}
      }
      setTimeout(updateNote, 150); return;
    }
    if (t.closest('[aria-controls="CartDrawer"], [aria-controls^="ShippingOption-"]')) { watchMount(); schedule(60); setTimeout(updateNote, 80); refreshMethod(1500); }
  }, true);
  document.addEventListener('cart:refresh', function () { watchMount(); schedule(150); refreshMethod(900); });

  /* v12: re-check after ANY cart write lands (ShipZip's line rewrites and the
     jokers-cart.js routing guard, which now rebuilds Express from the cart
     attributes after a quantity change). XHR only - our own reads use fetch. */
  (function () {
    var send = XMLHttpRequest.prototype.send, open = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (m, u) { this.__jkStdUrl = String(u || ''); return open.apply(this, arguments); };
    XMLHttpRequest.prototype.send = function () {
      if (/\/cart\/(change|update)/.test(this.__jkStdUrl || '')) {
        lastCartXhr = Date.now();
        this.addEventListener('loadend', function () { lastCartXhr = Date.now(); refreshMethod(600); });
      }
      return send.apply(this, arguments);
    };
  })();

  /* v24: on page load with items in the cart, keep ShipZip's prompt + ring
     masked until the first decision (cleared by done(); 4s failsafe). */
  if (cartHasLines()) { pending(true, 4000); }
  schedule(300);
})();
